import React, { useState, Dispatch, SetStateAction, useMemo } from 'react';

/*
 * ダイアログ表示サポート用フック
 *
 * 親ウィンドウ
 *
 *   // 戻り値として Response型が返るモーダル用変数定義
 *   // 表示状態管理用のフラグと状態管理用変数が返る
 *   const [flagDlg,actionDlg] = useModal<Response>();
 *    :
 *
 *   // フラグ状態でダイアログの表示の有無を決める
 *   // ダイアログの型はここで決まる。必ず状態管理用変数を引数として与える
 *   const dlg = flagDlg?<Dialog action={actionDlg}/>:null;
 *
 * 　return (
 *     <div>
 *       <Button onclick={(p)=>{
 *         // ダイアログオープン処理
 *         actionDlg.open()
 *           .apply((prm: Response)=>{
 *              // 確定時処理
 *           })
 *           .close(()=>{
 *              // キャンセル時処理
 *           });
 *       }}>Press</Button>
 *       {dlg}
 *     </div>
 *   );
 *
 * 子ウィンドウ
 *  // 子ウィンドウのプロパティに必ず状態管理用変数を定義しておく
 *  type DialogProps = {
 *    dialog: DialogAction<Response>;
 *  };
 *
 *  const Dialog = (props: DialogProps) => {
 *    // 確定でダイアログを終了する場合
 *    // 引数としてResponse型の値を渡す
 *    props.action.apply({});
 *
 *    // キャンセルでダイアログを終了する場合
 *    props.action.cancel();
 *  }
 */

/**
 * モーダルウィンドウ管理用フック
 * @param isOpen ウィンドウ初期状態。デフォルトはクローズ
 * @returns
 */
export function useModal<TResponse>(isOpen: boolean = false): [boolean, DialogAction<TResponse>] {
  const [flag, setFlag] = useState(isOpen);
  const dialogAction = useMemo(() => {
    return new DialogAction<TResponse>(setFlag);
  }, []);

  return [flag, dialogAction];
}

/**
 * ダイアログ操作クラス
 *
 */
export class DialogAction<TResponse> {
  private _setFlag: Dispatch<SetStateAction<boolean>>;
  private _applyAction?: (p: TResponse) => void;
  private _cancelAction?: (p: TResponse | undefined) => void;

  constructor(setFlag: Dispatch<SetStateAction<boolean>>) {
    this._setFlag = setFlag;
  }

  /**
   * オープン
   * @returns
   */
  open(): DialogDispatch<TResponse> {
    this._setFlag(true);

    return new DialogDispatch<TResponse>(this);
  }

  /**
   * クローズ
   * @description 何もせずにダイアログを閉じます
   */
  close() {
    this._setFlag(false);
  }

  /**
   * 確定処理
   * @param res パラメータ
   * @description パラメータを確定処理に渡し、ダイアログを閉じます。
   */
  apply(res: TResponse) {
    if (this._applyAction) {
      this._applyAction(res);
    }
    this.close();
  }

  /**
   * キャンセル処理
   * @param res パラメータ
   * @description パラメータをキャンセル処理に渡し、ダイアログを閉じます。
   */
  cancel(res?: TResponse) {
    if (this._cancelAction) {
      this._cancelAction(res);
    }
    this.close();
  }

  /**
   * 確定時処理登録
   * @param applyAction 確定時処理
   */
  setApply(applyAction: (p: TResponse) => void) {
    this._applyAction = applyAction;
  }

  /**
   * キャンセル時処理登録
   * @param cancelAction キャンセル時処理
   */
  setCancel(cancelAction: (p: TResponse | undefined) => void) {
    this._cancelAction = cancelAction;
  }
}

/**
 * ダイアログディスパッチャ
 */
class DialogDispatch<TResponse> {
  private _parent: DialogAction<TResponse>;

  /**
   * コンストラクタ
   * @param open オープンフラグ
   */
  constructor(parent: DialogAction<TResponse>) {
    this._parent = parent;
  }

  /**
   * 確定時処理設定
   * @param action 確定時処理
   * @description 確定時に呼び出される処理を登録します。
   */
  apply(action: (response: TResponse) => void): DialogDispatch<TResponse> {
    this._parent.setApply(action);

    return this;
  }

  /**
   * キャンセル時処理登録
   * @param action キャンセル時処理
   * @description キャンセル時に呼び出される処理を登録します。
   */
  cancel(action: (response: TResponse | undefined) => void): DialogDispatch<TResponse> {
    this._parent.setCancel(action);

    return this;
  }
}
